import React from 'react';
import { ASIN_METRIC_TYPE, COMPONENT_NAMES, METRIC_NAMES } from '../../../constants/asin_metric_elements';
import { COMPONENT_TO_LINK } from '../../../constants/urls';
import { capitalize } from '../../../utilities';
import ComponentLink from '../../content/controls/component_link';

const KEY_SEPARATOR = '-';

export const generateCompositeKey = (snapshotDate, metric, component) => {
  const keys = [snapshotDate, metric, component];
  return keys.join(KEY_SEPARATOR);
};

export const generateLabel = (compositeKey, displaySnapshotDate = false, displayMetric = false) => {
  const [snapshotDate, metric, component] = compositeKey.split(KEY_SEPARATOR);
  let labelKeys = [];
  if(displaySnapshotDate) labelKeys.push(snapshotDate);
  if(displayMetric) labelKeys.push(METRIC_NAMES[metric]);
  labelKeys.push(COMPONENT_NAMES[component]);
  return labelKeys.join(KEY_SEPARATOR);
};

export const extractComponent = (compositeKey) => {
  const [, , component] = compositeKey.split(KEY_SEPARATOR);
  return component;
};

export const extractMetric = (compositeKey) => {
  const [, metric, ] = compositeKey.split(KEY_SEPARATOR);
  return metric;
};

export const extractSnapshotDate = (compositeKey) => {
  const [snapshotDate, , ] = compositeKey.split(KEY_SEPARATOR);
  return snapshotDate;
};

export const extractMonth = extractSnapshotDate;

export const getComponentLink = (name) => {
  return (<ComponentLink componentName={COMPONENT_NAMES[name] || name} mapping={COMPONENT_TO_LINK} level={0} />);
};

export const formatValue = (value, displayInMillions) => {
  if(isNaN(value)){
    return 'N/A';
  } else if (displayInMillions) {
    return new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(value / 1000000);
  } else {
    return new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(value);
  }
};

export const filterLinesOrBars = (lines, selectedComponents, selectedMetrics, selectedMonths = null) => {
  let filteredLines = lines;
  if (selectedComponents) filteredLines = lines.filter(id => selectedComponents.some(comp => comp === extractComponent(id)));
  if (selectedMetrics) filteredLines = filteredLines.filter(id => selectedMetrics.some(metric => metric === extractMetric(id)));
  if (selectedMonths) filteredLines = filteredLines.filter(id => selectedMonths.some(month => month === extractMonth(id)));
  return filteredLines
};

export const getColumnDefinitions = (items, displayInMillions) => {
  let columnDefinition = [];
  const item = items && items.length > 0 ? items[0] : {};
  for(const key of Object.keys(item)){
    if(key === 'component'){
      columnDefinition.push({id: key, header: 'Component', cell: (item) => getComponentLink(item[key])});
    } else if (key === 'snapshot') {
      columnDefinition.push({id: key, header: 'As of Date', cell: (item) => item[key]});
    } else if([ASIN_METRIC_TYPE.NET_FA, ASIN_METRIC_TYPE.NET_LA,
               ASIN_METRIC_TYPE.NET_VARIANCE, ASIN_METRIC_TYPE.ABS_VARIANCE,
               ASIN_METRIC_TYPE.FA_UNITS, ASIN_METRIC_TYPE.LA_UNITS].includes(key)) {
      columnDefinition.push({id: key, header: METRIC_NAMES[key], cell: (item) => formatValue(item[key], displayInMillions)});
    } else if (key === 'ASIN'){
      columnDefinition.push({id: key, header: 'ASIN', cell: (item) => (
        <a href={"https://www.amazon.com/s?k=" + item[key]} target="_blank"> {item[key]} </a>
        )});
    } else {
      columnDefinition.push({id: key, header: METRIC_NAMES[key] || capitalize(key),
                              cell: (item) => !isNaN(item[key]) ? item[key].toFixed(2) : 'N/A'});
    }
  }
  return columnDefinition;
};

export const mergeKeysOfObjectsWithSameId = (data, id) => {
  return Object.values(data.reduce((c, v) => {
    c[v[id]] = {...c[v[id]], ...v};
    return c;
  }, {}));
};

export const getComponentMetricMap = (items) => {
  // snapshot => Map(component => Map(metric=> metric value))
  const latestSnapshot = items.map(item => parseInt(item.snapshot))
                              .reduce((currentLatestSnapshot, snapshot) =>
                                        Math.max(currentLatestSnapshot, snapshot), 0);

  let snapshotToComponentMetricsMap = new Map();
  for(const item of items){
    const snapshot = item.snapshot;
    if(!snapshotToComponentMetricsMap.has(snapshot)){
      snapshotToComponentMetricsMap.set(snapshot, new Map());
    }
    let componentToMetricsMap = snapshotToComponentMetricsMap.get(snapshot);
    const componentValues = item.componentValues;
    const metric = item.metric;
    const units = item.units;
    if(componentValues){
      for (const component of Object.keys(componentValues)){
        if(!componentToMetricsMap.has(component)){
          componentToMetricsMap.set(component, new Map());
        }
        let metricsMap = componentToMetricsMap.get(component);
        if(!metricsMap.has(metric)){
          metricsMap.set(metric, componentValues[component]);
        }
        /** Although FA_UNITS and LA_UNITS are not linked to components (they are linked to gls)
         * We are mapping them to components here so that in GL Metric tables,
         * we have FA_UNITS and LA_UNITS columns for each component.
         */
        if(metric === ASIN_METRIC_TYPE.NET_FA){
          metricsMap.set(ASIN_METRIC_TYPE.FA_UNITS, units);
        } else if (metric === ASIN_METRIC_TYPE.NET_LA) {
          metricsMap.set(ASIN_METRIC_TYPE.LA_UNITS, units);
        }
      }
    }
  }
  return [snapshotToComponentMetricsMap, latestSnapshot.toString()];
}

//TODO point glMetricTrend to use this method after /asinMetric is changed to use snapshot instead of snapshotDate
export const getTrendGraphSnapshotMap = (metricItems) => {
  let snapshotMap = new Map();
  for(const metricItem of metricItems){
    const {snapshotDate, metric, month, componentValues} = metricItem;
    const snapshot = snapshotDate;
    const trendData = Object.keys(componentValues).reduce( (acc, comp) => {
      acc[generateCompositeKey(month, metric, comp)] = componentValues[comp];
      return acc;
    }, {});
    if(!snapshotMap.has(snapshot)) snapshotMap.set(snapshot, {snapshot});
    let snapshotDateRow = snapshotMap.get(snapshot);
    Object.assign(snapshotDateRow, trendData);
  }
  return snapshotMap;
}


// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/asin_metrics_utils.jsx